import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Card,
  CardBody,
  Badge,
  Divider,
  Box,
  Spinner,
  Center,
  HStack,
  IconButton,
  useToast,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Flex,
  Heading,
  Avatar,
  Stack,
  useColorModeValue
} from '@chakra-ui/react'
import { observer } from 'mobx-react'
// Simple markdown renderer without external dependencies
import { getPublishedReleaseNotes } from 'API/releaseNote'

interface ReleaseNote {
  id?: number
  version: string
  title: string
  content: string
  releaseDate: string
  published: boolean
  sortOrder: number
}

interface ReleaseNotesModalProps {
  isOpen: boolean
  onClose: () => void
}

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ isOpen, onClose }) => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([])
  const [selectedReleaseNote, setSelectedReleaseNote] = useState<ReleaseNote | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const toast = useToast()

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const hoverBg = useColorModeValue('gray.50', 'gray.700')

  useEffect(() => {
    if (isOpen) {
      fetchReleaseNotes()
    }
  }, [isOpen])

  const fetchReleaseNotes = async () => {
    try {
      setIsLoading(true)
      const notes = await getPublishedReleaseNotes()
      setReleaseNotes(notes)
      // Select the first release note by default
      if (notes.length > 0) {
        setSelectedReleaseNote(notes[0])
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load release notes',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setIsLoading(false)
    }
  }

  const formatReleaseContent = (content: string) => {
    return content.split('\n').map((line, index) => (
      <Text key={index} mb={2}>
        {line}
      </Text>
    ))
  }

  // Simple markdown renderer
  const renderMarkdown = (content: string) => {
    const lines = content.split('\n')
    const elements: React.ReactElement[] = []
    let key = 0

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]

      // Headers
      if (line.startsWith('### ')) {
        elements.push(
          <Heading key={key++} size="sm" mb={2} mt={4}>
            {line.substring(4)}
          </Heading>
        )
      } else if (line.startsWith('## ')) {
        elements.push(
          <Heading key={key++} size="md" mb={3} mt={4}>
            {line.substring(3)}
          </Heading>
        )
      } else if (line.startsWith('# ')) {
        elements.push(
          <Heading key={key++} size="lg" mb={4} mt={4}>
            {line.substring(2)}
          </Heading>
        )
      }
      // Bullet points
      else if (line.startsWith('- ') || line.startsWith('* ')) {
        elements.push(
          <Box key={key++} as="li" ml={4} mb={1}>
            {line.substring(2)}
          </Box>
        )
      }
      // Numbered lists
      else if (/^\d+\.\s/.test(line)) {
        elements.push(
          <Box key={key++} as="li" ml={4} mb={1}>
            {line.replace(/^\d+\.\s/, '')}
          </Box>
        )
      }
      // Code blocks
      else if (line.startsWith('```')) {
        const codeLines: string[] = []
        i++ // Skip the opening ```
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i])
          i++
        }
        elements.push(
          <Box
            key={key++}
            as="pre"
            bg="gray.100"
            p={4}
            borderRadius="md"
            overflowX="auto"
            mb={3}
            fontSize="sm"
            fontFamily="mono"
          >
            {codeLines.join('\n')}
          </Box>
        )
      }
      // Regular paragraphs
      else if (line.trim()) {
        elements.push(
          <Text key={key++} mb={3} lineHeight="1.6">
            {line}
          </Text>
        )
      }
      // Empty lines
      else {
        elements.push(<Box key={key++} mb={2} />)
      }
    }

    return elements
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent maxHeight="90vh" margin="20px">
          <ModalHeader>
            <HStack justify="space-between" width="full">
              <Heading size="lg">Release Notes</Heading>
              <HStack>
                <ModalCloseButton />
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalBody p={0}>
            {isLoading ? (
              <Center py={8}>
                <Spinner size="lg" />
              </Center>
            ) : (
              <Flex height="calc(90vh - 120px)">
                {/* Left Sidebar - Release Notes List */}
                <Box width="350px" borderRight="1px solid" borderColor={borderColor} bg={bgColor} overflowY="auto">
                  <VStack spacing={0} align="stretch">
                    {releaseNotes.length === 0 ? (
                      <Box p={6} textAlign="center">
                        <Text color="gray.500" fontSize="sm">
                          No release notes found. Click "Add Release Note" to create the first one.
                        </Text>
                      </Box>
                    ) : (
                      releaseNotes.map(note => (
                        <Box
                          key={note.id}
                          p={4}
                          borderBottom="1px solid"
                          borderColor={borderColor}
                          cursor="pointer"
                          bg={selectedReleaseNote?.id === note.id ? 'blue.50' : 'transparent'}
                          _hover={{ bg: selectedReleaseNote?.id === note.id ? 'blue.50' : hoverBg }}
                          onClick={() => setSelectedReleaseNote(note)}
                        >
                          <VStack align="start" spacing={2}>
                            <HStack spacing={2}>
                              <Badge colorScheme="green" size="sm">
                                v{note.version}
                              </Badge>
                              {!note.published && (
                                <Badge colorScheme="orange" size="sm">
                                  Draft
                                </Badge>
                              )}
                            </HStack>
                            <Text fontSize="md" fontWeight="semibold" noOfLines={2}>
                              {note.title}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(note.releaseDate).toLocaleDateString()}
                            </Text>
                          </VStack>
                        </Box>
                      ))
                    )}
                  </VStack>
                </Box>

                {/* Right Side - Release Note Detail */}
                <Box flex={1} bg={bgColor} overflowY="auto">
                  {selectedReleaseNote ? (
                    <Box p={8}>
                      <VStack align="start" spacing={6}>
                        {/* Header */}
                        <VStack align="start" spacing={3} width="full">
                          <HStack spacing={3}>
                            <Badge colorScheme="green" fontSize="sm" px={3} py={1}>
                              ✅ Released in v{selectedReleaseNote.version}
                            </Badge>
                            {!selectedReleaseNote.published && (
                              <Badge colorScheme="orange" fontSize="sm" px={3} py={1}>
                                Draft
                              </Badge>
                            )}
                          </HStack>
                          <Heading size="xl">{selectedReleaseNote.title}</Heading>
                          <Text color="gray.500" fontSize="sm">
                            Released on{' '}
                            {new Date(selectedReleaseNote.releaseDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Text>
                        </VStack>

                        <Divider />

                        {/* Content */}
                        <Box width="full">{renderMarkdown(selectedReleaseNote.content)}</Box>
                      </VStack>
                    </Box>
                  ) : (
                    <Center height="full">
                      <VStack spacing={4}>
                        <Text fontSize="lg" color="gray.500">
                          Select a release note to view details
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          Choose from the list on the left to see the full release notes
                        </Text>
                      </VStack>
                    </Center>
                  )}
                </Box>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default observer(ReleaseNotesModal)
